import * as Sentry from '@sentry/react'
import axios, { type AxiosRequestConfig } from 'axios'
import { getRuntimeConfig, type ServiceName } from './runtime-config'
import { toApiError } from './api-error'
import { getLocalDevelopmentAccessToken } from '@/config/local-development-auth'
import { profileApiPath } from '@/config/profile-api'
import { isDemoDataRoute } from '@/demo/microservice/config'

const enabledApiPaths = new Set([profileApiPath])

const cicdConfigurationPath = /^\/v1\/api\/cicd-config\/[^/]+\/all$/
const v2CicdPath = /^\/v2\/cicd(?:\/|$)/
const codeReviewPath = /^\/v1\/api\/code-reviews(?:\/|$)/
const databaseConnectionTestPath = '/api/v1/database/test-connection'
const forgeSphereLogSearchPath = '/v1/api/forgesphere-logs/search'
const adhocConnectionTestPath = '/adhoc/execute'
const v2ResourcePath = '/v2/api/resources'
const v2ResourceItemPath = /^\/v2\/api\/resources\/[^/]+$/
const v2ResourceReadPath =
  /^\/v2\/api\/resources\/[^/]+\/(onboarding|connector|connector\/readiness|steps)$/
const v2ResourceCommandPath =
  /^\/v2\/api\/resources\/[^/]+\/(archive|restore|onboarding\/complete)$/
const v2StepStatusPath = /^\/v2\/api\/resources\/[^/]+\/steps\/[^/]+\/status$/
const v2ConnectorPath = /^\/v2\/api\/resources\/[^/]+\/connector$/
const v2PlatformCollectionPath =
  /^\/v2\/api\/platform-onboarding\/(business-units|projects|applications|consumers)$/
const v2PlatformApplicationPath =
  /^\/v2\/api\/platform-onboarding\/applications\/[^/]+$/
const v2PlatformConsumersPath =
  /^\/v2\/api\/platform-onboarding\/applications\/[^/]+\/consumers$/
const v2RequirementPath = /^\/v2\/api\/resources\/[^/]+\/requirements$/
const v2RequirementCommandPath =
  /^\/v2\/api\/resources\/[^/]+\/requirements\/(complete|skip|spec-recommendations)$/
const v2ApiDesignResourcePath =
  /^\/v2\/api\/resources\/[^/]+\/api-design(?:\/specifications|\/generated-specification)?$/
const v2ValidationRunsPath =
  /^\/v2\/api\/resources\/[^/]+\/api-design\/validation-runs$/
const v2SpecificationPath =
  /^\/v2\/api\/specifications(?:\/library|\/[^/]+(?:\/content|\/promote|\/endpoints)?)$/
const v2SchemaPath = /^\/v2\/api\/schemas(?:\/[^/]+)?$/
const v2ApiDevelopmentPath =
  /^\/v2\/api\/resources\/[^/]+\/api-development(?:\/artifact\/download|\/operations(?:\/latest|\/[^/]+(?:\/events|\/cancel)?)?)?$/
const v2DeploymentPath =
  /^\/v2\/api\/resources\/[^/]+\/deployments(?:\/artifacts(?:\/sync)?|\/nexus-versions|\/history|\/(?:promote|rollback)(?:\/github)?|\/[^/]+\/github\/status)$/
const v2ResourceCopyPath =
  /^\/v2\/api\/resources\/[^/]+\/copy-operations(?:\/latest|\/[^/]+(?:\/events)?)?$/
const migrationConnectorViewsPath =
  /^\/v1\/api\/connector-configurations\/organization\/[^/]+\/migration-views$/
const apigeeWrapperPath =
  /^\/(?:auth\/apigee\/token|organizations(?:\/.*)?|profiles(?:\/test-connection)?)$/
const kongWrapperPath =
  /^\/(?:config\/konnect\/(?:profiles|verify)|v2\/control-planes(?:\/.*)?)$/

export const httpClient = axios.create({
  timeout: 30_000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
})

const CORRELATION_APPLICATION_NAME = 'forgesphere-lifecycle-fe'
const CORRELATION_APPLICATION_HASH = hashText(CORRELATION_APPLICATION_NAME)

httpClient.interceptors.request.use((request) => {
  const developmentAccessToken = getLocalDevelopmentAccessToken()
  const profileRequest = isProfileApiRequest(request.url)
  const apigeeTokenRequest =
    getRequestPath(request.url ?? '') === '/auth/apigee/token'
  request.headers.set('X-Correlation-Id', createCorrelationId())
  request.headers.set('X-Partner-Id', getPartnerId())
  if (
    developmentAccessToken &&
    !profileRequest &&
    !apigeeTokenRequest &&
    !request.headers.has('Authorization')
  ) {
    request.headers.set('Authorization', `Bearer ${developmentAccessToken}`)
  } else if (profileRequest || apigeeTokenRequest) {
    request.headers.delete('Authorization')
  }
  if (
    isDemoDataRoute() &&
    !isEnabledApiRequest(request.url, request.method) &&
    typeof request.adapter !== 'function'
  ) {
    request.adapter = async (config) => ({
      data: {},
      status: 200,
      statusText: 'Local preview',
      headers: {},
      config,
    })
  }
  return request
})

export function isEnabledApiRequest(
  url: string | undefined,
  method: string | undefined,
) {
  if (!url) return false
  const path = getRequestPath(url)
  if (!path) return false
  const requestMethod = (method ?? 'GET').toUpperCase()
  if (apigeeWrapperPath.test(path)) return true
  if (kongWrapperPath.test(path)) return true
  if (v2CicdPath.test(path)) return true
  if (codeReviewPath.test(path)) return true
  if (requestMethod === 'GET' && path === v2ResourcePath) return true
  if (requestMethod === 'GET' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'GET' && v2ResourceReadPath.test(path)) return true
  if (requestMethod === 'GET' && v2RequirementPath.test(path)) return true
  if (requestMethod === 'GET' && v2ApiDesignResourcePath.test(path)) return true
  if (requestMethod === 'GET' && v2ValidationRunsPath.test(path)) return true
  if (requestMethod === 'GET' && v2SpecificationPath.test(path)) return true
  if (requestMethod === 'GET' && v2SchemaPath.test(path)) return true
  if (requestMethod === 'GET' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'GET' && v2DeploymentPath.test(path)) return true
  if (requestMethod === 'GET' && v2ResourceCopyPath.test(path)) return true
  if (requestMethod === 'GET' && v2PlatformCollectionPath.test(path))
    return true
  if (requestMethod === 'GET' && v2PlatformApplicationPath.test(path))
    return true
  if (requestMethod === 'GET' && migrationConnectorViewsPath.test(path))
    return true
  if (requestMethod === 'POST' && path === v2ResourcePath) return true
  if (requestMethod === 'POST' && v2ResourceCommandPath.test(path)) return true
  if (requestMethod === 'POST' && v2StepStatusPath.test(path)) return true
  if (requestMethod === 'POST' && v2RequirementCommandPath.test(path))
    return true
  if (requestMethod === 'POST' && v2ApiDesignResourcePath.test(path))
    return true
  if (requestMethod === 'POST' && v2ValidationRunsPath.test(path)) return true
  if (requestMethod === 'POST' && v2SpecificationPath.test(path)) return true
  if (requestMethod === 'POST' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'POST' && v2DeploymentPath.test(path)) return true
  if (requestMethod === 'POST' && v2ResourceCopyPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ResourceCommandPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ConnectorPath.test(path)) return true
  if (requestMethod === 'PUT' && v2RequirementPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ApiDesignResourcePath.test(path)) return true
  if (requestMethod === 'PUT' && v2SpecificationPath.test(path)) return true
  if (requestMethod === 'PUT' && v2SchemaPath.test(path)) return true
  if (requestMethod === 'PUT' && v2ApiDevelopmentPath.test(path)) return true
  if (requestMethod === 'PUT' && v2PlatformConsumersPath.test(path)) return true
  if (requestMethod === 'PATCH' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'PATCH' && v2PlatformApplicationPath.test(path))
    return true
  if (requestMethod === 'DELETE' && v2ResourceItemPath.test(path)) return true
  if (requestMethod === 'POST') {
    return (
      path === databaseConnectionTestPath ||
      path === adhocConnectionTestPath ||
      path === forgeSphereLogSearchPath
    )
  }
  if (requestMethod !== 'GET') return false
  return (
    [...enabledApiPaths].some((enabledPath) => path.endsWith(enabledPath)) ||
    cicdConfigurationPath.test(path)
  )
}

export function isProfileApiRequest(url: string | undefined) {
  if (!url) return false
  const path = getRequestPath(url)
  return path.endsWith(profileApiPath)
}

function getRequestPath(url: string) {
  try {
    return new URL(url, 'https://forgesphere.local').pathname.replace(/\/$/, '')
  } catch {
    return url.split('?')[0]?.replace(/\/$/, '') ?? ''
  }
}

export function createCorrelationId() {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, '')
  const requestEntropy = globalThis.crypto.randomUUID().slice(0, 8)
  return `${timestamp}-${CORRELATION_APPLICATION_HASH}-${requestEntropy}`
}

function hashText(value: string) {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function getPartnerId() {
  if (typeof window === 'undefined') return 'probestack'
  return (window.localStorage.getItem('companyName') || 'probestack')
    .trim()
    .toLowerCase()
}

export function getAuthenticatedRequestHeaders(): Record<string, string> {
  const developmentAccessToken = getLocalDevelopmentAccessToken()
  return {
    'X-Correlation-Id': createCorrelationId(),
    ...(developmentAccessToken
      ? { Authorization: `Bearer ${developmentAccessToken}` }
      : {}),
    'X-Partner-Id': getPartnerId(),
  }
}

httpClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error)
    Sentry.captureException(apiError, {
      tags: {
        errorKind: apiError.kind,
        status: String(apiError.status ?? 'none'),
      },
      extra: {
        correlationId: apiError.correlationId,
      },
    })
    return Promise.reject(apiError)
  },
)

export function serviceRequest<T>(
  service: ServiceName,
  request: AxiosRequestConfig,
) {
  const enabledRequest = isEnabledApiRequest(request.url, request.method)
  const liveRequest =
    enabledRequest &&
    (service === 'apiTest' ||
      service === 'apigee' ||
      service === 'kong' ||
      (!isDemoDataRoute() &&
        (service === 'onboarding' ||
          service === 'cicdAutomation' ||
          service === 'codeReview' ||
          service === 'requirements' ||
          service === 'apiDesign' ||
          service === 'apiDevelopment')))
  const demoRequest = isDemoDataRoute() && !liveRequest

  return httpClient.request<T>({
    ...request,
    baseURL: getRuntimeConfig().services[service] ?? '/mcp-generate',
    ...(demoRequest
      ? {
          adapter: async (config) => {
            const { handleDemoRequest } =
              await import('@/demo/microservice/handler')
            const data = await handleDemoRequest(service, config)
            return { data, status: 200, statusText: 'OK', headers: {}, config }
          },
        }
      : {}),
  })
}
